/**
 * LivretAccessBanner.jsx — Bandeau d'accès direct au livret interactif.
 *
 * À afficher en tête des pages /epreuves/plateau et /epreuves/circulation
 * pour donner un accès en 1 tap au livret correspondant (utilisable
 * sans compte).
 *
 * 100% mobile-first : grande zone tap, contraste élevé, badge visible.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, ArrowRight, CloudUpload } from 'lucide-react';

/**
 * @param {string} livretTo    Route du livret (ex: /livret-plateau)
 * @param {string} livretName  Ex: "Livret Plateau A1/A2"
 * @param {string} desc        Ex: "Coche ta progression après chaque séance"
 * @param {string} color       Couleur d'accent (ex: '#e11d48')
 */
export default function LivretAccessBanner({
  livretTo,
  livretName = 'Livret interactif',
  desc = 'Coche ta progression après chaque séance',
  color = '#e11d48',
}) {
  return (
    <Link
      to={livretTo}
      className="group block rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-[#141416] to-[#141416] p-4 sm:p-5 hover:border-emerald-500/60 active:scale-[0.99] transition print:hidden"
      data-testid="livret-access-banner"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-300" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9.5px] font-black uppercase tracking-widest">
              Livret interactif
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-white/50">
              <CloudUpload className="w-3 h-3" /> Accessible sans compte
            </span>
          </div>
          <div className="text-white font-black text-base sm:text-lg leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {livretName}
          </div>
          <div className="text-white/60 text-[12.5px] leading-relaxed mt-0.5">{desc}</div>
        </div>
        <ArrowRight className="w-5 h-5 text-emerald-300 flex-shrink-0 group-hover:translate-x-1 transition" />
      </div>
    </Link>
  );
}
